// RingCentral Call Control from the browser, with the person's OWN sign-in
// (the phone app, Call Control on it). Only a call's owner may control it
// (RingCentral TAS-106 "Operation is not allowed" for anyone else, live test
// 2026-11-04), which is why the server never does this: it checks and
// records, the browser moves.
import { RC_SERVER_URL } from './config';
import { ensureAuthorized, getAccessToken, getStoredTokens } from './auth';

export const CALL_CONTROL_COPY = {
  signIn: 'Sign in to RingCentral in the app (the chip in the header) to transfer from here.',
  relogin:
    'Sign out of RingCentral in the app and back in once, so your sign-in carries the Call Control permission.',
  refused: 'RingCentral refused the transfer. It stays on your phone.',
  gone: 'That call is over.',
};

/**
 * Blind-transfer my party of a live call to an extension or a number.
 * Docs: POST /restapi/v1.0/account/~/telephony/sessions/{id}/parties/{partyId}/transfer
 * Throws { code: 'no_token' | 'relogin' | 'gone' | 'refused', message, status }.
 */
export const transferMyParty = async ({ telephonySessionId, partyId, extensionNumber = null, phoneNumber = null }) => {
  if (!getStoredTokens()?.access_token) throw Object.assign(new Error(CALL_CONTROL_COPY.signIn), { code: 'no_token' });
  if (!(await ensureAuthorized())) throw Object.assign(new Error(CALL_CONTROL_COPY.signIn), { code: 'no_token' });
  const body = phoneNumber ? { phoneNumber: String(phoneNumber) } : { extensionNumber: String(extensionNumber) };
  const res = await fetch(
    `${RC_SERVER_URL}/restapi/v1.0/account/~/telephony/sessions/${encodeURIComponent(telephonySessionId)}/parties/${encodeURIComponent(partyId)}/transfer`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAccessToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (res.ok) return true;
  let detail = null;
  try {
    detail = await res.json();
  } catch (_) {
    detail = null;
  }
  const rcCode = detail?.errorCode || detail?.errors?.[0]?.errorCode || null;
  const rcMessage = detail?.message || detail?.errors?.[0]?.message || '';
  // 401, or a 403 naming the missing permission: the token predates Call
  // Control. A 403 TAS-106 is RingCentral refusing the operation itself.
  const code =
    res.status === 401 || (res.status === 403 && /permission/i.test(rcMessage))
      ? 'relogin'
      : res.status === 404
        ? 'gone'
        : 'refused';
  throw Object.assign(new Error(CALL_CONTROL_COPY[code] || CALL_CONTROL_COPY.refused), { code, status: res.status, rcCode, rcMessage });
};
